const _ = require('lodash');
const { reverse } = require('named-urls');
const memoizeOneModule = require('memoize-one');
const routes = require('./routes');

const memoizeOne = memoizeOneModule.default || memoizeOneModule;

const recursivelyInflateContent = (input) => {
  const {
    product,
    content,
    contentTitlePrefix,
    contentParentUuid,
    contentParent,
    skipParent,
  } = input;

  const { slug: productSlug } = product;
  const contentByParent = _.groupBy(content, 'parentUuid');

  const contentAtLvl = contentByParent[contentParentUuid];
  const sortedContent = _.sortBy(contentAtLvl, 'position');

  const inflatedContent = sortedContent.map((contentInstance, i) => {
    const key = `content-${contentInstance.uuid}`;
    const titlePrefix = (() => {
      if (!contentTitlePrefix) {
        return `${i + 1}.`;
      }

      if (contentTitlePrefix.endsWith('.')) {
        return `${contentTitlePrefix}${i + 1}`;
      }

      return `${contentTitlePrefix}.${i + 1}`;
    })();
    const title = `${titlePrefix} ${contentInstance.name}`;
    const path = reverse(routes.study.learn, {
      productSlug,
      contentUuid: contentInstance.slug,
    });

    const toReturn = {
      ...contentInstance,
      key,
      titlePrefix,
      title,
      path,
    };

    if (!skipParent) {
      toReturn.parent = contentParent;
    }

    const inflatedChildContent = recursivelyInflateContent({
      product,
      content,
      contentTitlePrefix: titlePrefix,
      contentParentUuid: contentInstance.uuid,
      contentParent: toReturn,
      skipParent,
    });

    toReturn.content = inflatedChildContent;

    return toReturn;
  });

  return inflatedContent;
};

const recursivelyFlattenContent = ({ inflatedContent }) => {
  if (_.isEmpty(inflatedContent)) {
    return [];
  }

  const lvlFlattenedContent = inflatedContent.reduce((acc, obj) => {
    acc.push(obj);
    acc.push(
      ...recursivelyFlattenContent({
        inflatedContent: obj.content,
      })
    );
    return acc;
  }, []);

  return lvlFlattenedContent;
};

const inflateContent = ({ product, content }) => {
  const inflatedContent = recursivelyInflateContent({
    product,
    content,
    contentTitlePrefix: null,
    contentParentUuid: null,
  });

  const flatContent = recursivelyFlattenContent({
    inflatedContent,
  });

  return {
    flatContent,
    inflatedContent,
  };
};

const recursiveInfo = (node) => {
  const ourInfo = {
    uuids: [node.uuid],
    itemsReady: node.itemsReady,
    itemsAvailable: node.itemsAvailable,
  };

  if (_.isEmpty(node.content)) {
    return ourInfo;
  }

  const childrenInfo = _.flatten(
    node.content.map((child) => {
      return recursiveInfo(child);
    })
  );

  const result = childrenInfo.reduce((acc, child) => {
    acc.uuids = acc.uuids.concat(child.uuids);
    acc.itemsReady += child.itemsReady;
    acc.itemsAvailable += child.itemsAvailable;
    return acc;
  }, ourInfo);

  return result;
};

const countItemsAvailable = memoizeOne(({ contentUuids, content }) => {
  const contentByUuid = _.keyBy(content, 'uuid');
  const count = contentUuids.reduce((acc, contentUuid) => {
    const thisCount = _.get(contentByUuid, [contentUuid, 'itemsAvailable'], 0);
    return acc + thisCount;
  }, 0);
  return count;
});

const firstContentPath = ({ content }) => {
  if (!content.content || content.content.length === 0) {
    return content.path;
  }

  return firstContentPath({ content: content.content[0] });
};

const fullName = ({ content, contentByUuid }) => {
  const { name, parentUuid } = content;

  if (!content.parentUuid) {
    return name;
  }

  const parent = contentByUuid[parentUuid];
  if (!parent) {
    return name;
  }

  return `${name} | ${fullName({ content: parent, contentByUuid })}`;
};

module.exports = {
  inflateContent,
  recursivelyFlattenContent,
  recursivelyInflateContent,
  recursiveInfo,
  countItemsAvailable,
  firstContentPath,
  fullName,
};
